/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomCheckboxMultiSelect.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CheckBoxBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
function CustomCheckboxMultiSelect({
  selectedValues,
  setSelectedValues,
  optionsList,
  required,
  label,
  txtFldStyle = {},
  renderOption,
  getOptionLabel,
  infoname,
  tag,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  console.log(selectedValues, "selectedValues");
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const toggleOption = (option) => {
    const exists = selectedValues.some((v) => v.value === option.value);

    if (exists) {
      setSelectedValues(selectedValues.filter((v) => v.value !== option.value));
    } else {
      setSelectedValues([...selectedValues, option]);
    }
  };

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d9d9d9", borderRadius: 0 },
      "&:hover fieldset": { borderColor: "#8c8c8c" },
      "&.Mui-focused fieldset": { borderColor: "#000000" },
      "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
      "&.Mui-error fieldset": { borderColor: "#e53935" },
    },
    ...txtFldStyle,
  };
  const getDisplayValue = () => {
    if (!selectedValues || selectedValues.length === 0) return selectedValues;

    if (tag !== "other") {
      return `${selectedValues.length} Selected Vehicle Class`;
    }

    return selectedValues.map((item) => item.label).join(", ");
  };

  useEffect(() => {
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return;

    if (typeof selectedValues[0] === "object") return;

    const normalized = optionsList.filter((opt) =>
      selectedValues.includes(opt.value),
    );

    if (normalized.length > 0) {
      setSelectedValues(normalized);
    }
  }, [optionsList]);
  return (
    <div>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex" }}>
          <Typography
            variant="subtitle1"
            sx={{ color: "#6f7174", fontSize: "0.9rem", fontWeight: 500 }}
          >
            {label} <span style={{ color: "red" }}>{required ? "*" : ""}</span>
          </Typography>
        </Box>

        <TextField
          onClick={handleOpen}
          size="small"
          placeholder="Select"
          sx={textFieldStyle}
          value={getDisplayValue()}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
                {open ? (
                  <ArrowDropUpIcon sx={{ cursor: "pointer" }} />
                ) : (
                  <ArrowDropDownIcon sx={{ cursor: "pointer" }} />
                )}
              </Box>
            ),
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: { width: anchorEl?.clientWidth || 300, maxHeight: 300 },
          }}
        >
          <Box
            sx={{
              p: 1,
              maxHeight: 250,
              overflowY: "auto", // ✔ Only vertical scrollbar
            }}
          >
            {optionsList.map((option) => {
              const isChecked = selectedValues.some(
                (v) => v.value === option.value,
              );

              return (
                <MenuItem
                  key={option.value}
                  onClick={() => toggleOption(option)}
                  sx={{
                    display: "flex",
                    gap: 1,
                    whiteSpace: "normal", // ✔ Allows wrap
                    wordBreak: "break-word", // ✔ Long words wrap
                    "& .MuiSvgIcon-root": {
                      fontWeight: "bold",
                      width: 18,
                      height: 18,
                      overflow: "hidden",
                      transition: "background-color 0.2s, color 0.2s",
                    },
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    icon={<CheckBoxBlankIcon sx={{ color: "black" }} />}
                    checkedIcon={<CheckBoxIcon sx={{ color: "#26890D" }} />}
                    sx={{
                      padding: 0,
                      "& .MuiSvgIcon-root": { fontSize: 22 },
                    }}
                  />

                  <ListItemText primary={option.label} />
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      </Box>
    </div>
  );
}

CustomCheckboxMultiSelect.propTypes = {
  selectedValues: PropTypes.array,
  setSelectedValues: PropTypes.func,
  optionsList: PropTypes.array,
  required: PropTypes.bool,
  label: PropTypes.node,
  txtFldStyle: PropTypes.object,
  renderOption: PropTypes.func,
  getOptionLabel: PropTypes.func,
  infoname: PropTypes.string,
  tag: PropTypes.string,
};

CustomCheckboxMultiSelect.defaultProps = {
  selectedValues: [],
  setSelectedValues: () => {},
  optionsList: [],
  required: false,
  label: "",
  txtFldStyle: {},
  renderOption: null,
  getOptionLabel: null,
  infoname: "",
  tag: "",
};

export default CustomCheckboxMultiSelect;
